import { Injectable } from '@angular/core';

/**
 * InstallService — Captures the `beforeinstallprompt` event and exposes
 * it so any component can show an "Install App" popup on mobile.
 *
 * Usage:
 *   1. Inject InstallService in your component.
 *   2. Check `installService.canInstall` — true when the browser offers PWA install.
 *   3. Show your own banner / popup.
 *   4. Call `installService.promptInstall()` to trigger the native prompt.
 *   5. After install, `canInstall` becomes false.
 */
@Injectable({ providedIn: 'root' })
export class InstallService {
  private deferredPrompt: any = null;
  canInstall = false;
  dismissed = false;            // user tapped "Not now"
  installed = false;            // already installed or prompt accepted
  isIos = false;
  isStandalone = false;

  constructor() {
    // Detect iOS Safari (no beforeinstallprompt event)
    const ua = navigator.userAgent || '';
    this.isIos = /iphone|ipad|ipod/i.test(ua) && !(window as any).MSStream;
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;

    // If already running as installed app, don't show prompt
    if (this.isStandalone) {
      this.installed = true;
      return;
    }

    // Check if user previously dismissed in this session
    if (sessionStorage.getItem('pwa_install_dismissed') === '1') {
      this.dismissed = true;
    }

    // For Android / Chrome / Edge — capture the deferred prompt
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.canInstall = true;
    });

    // Detect successful install
    window.addEventListener('appinstalled', () => {
      this.installed = true;
      this.canInstall = false;
      this.deferredPrompt = null;
    });

    // On iOS, show manual instructions instead
    if (this.isIos && !this.isStandalone) {
      this.canInstall = true;
    }
  }

  /** Whether the install banner should be shown right now */
  get shouldShowBanner(): boolean {
    return this.canInstall && !this.dismissed && !this.installed && !this.isStandalone;
  }

  /** Trigger the native install prompt (Android/Chrome).
   *  On iOS, this is a no-op — the UI should show manual instructions. */
  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) return false;
    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      this.installed = true;
      this.canInstall = false;
    }
    this.deferredPrompt = null;
    return result.outcome === 'accepted';
  }

  /** User tapped "Not now" — hide for this session */
  dismiss(): void {
    this.dismissed = true;
    sessionStorage.setItem('pwa_install_dismissed', '1');
  }
}
